import {
  CIRCUIT_EVENT_SOURCES,
  CIRCUIT_EVENT_TEXT_OPERATORS,
  getCircuitConditionFields,
  getCircuitEventEntityTypes,
  getCircuitEventTypes,
} from "@sherlog/circuits/event-contract";
import {
  CIRCUIT_WEBHOOK_MAX_FILTER_FIELD_LENGTH,
  CIRCUIT_WEBHOOK_MAX_FILTER_RULES,
} from "@sherlog/circuits/webhook-filter";
import {
  Bot,
  Braces,
  CalendarClock,
  Clipboard,
  GitBranch,
  KeyRound,
  Plus,
  Radio,
  Save,
  Timer,
  Trash2,
  X,
} from "lucide-react";
import { useDeferredValue, useState } from "react";

import { useCreateCircuit, useRotateCircuitWebhook, useUpdateCircuit } from "@/hooks/useCircuits";
import { useModels } from "@/hooks/useModels";
import { useStore } from "@/lib/store";
import type {
  Circuit,
  CircuitDefinition,
  CircuitCronOverlapPolicy,
  CircuitEdge,
  CircuitEventSource,
  CircuitNode,
  CircuitTriggerConfig,
  ManagedOwnerScope,
  ModelOption,
} from "@/lib/types";

import { SidebarToggleButton } from "../SidebarToggleButton";
import { CircuitGraph, type CircuitInsertLocation } from "./CircuitGraph";

type CircuitEditorProps = {
  circuit?: Circuit;
  ownerScope: ManagedOwnerScope;
  onClose: () => void;
  onSaved: (circuit: Circuit) => void;
};

const inputClass =
  "border-cyber-border bg-cyber-bg text-cyber-text placeholder:text-cyber-muted/60 focus:border-cyber-cyan/60 w-full rounded border px-3 py-2 text-sm outline-none transition-colors";
const defaultTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC";

function eventOptionLabel(value: string): string {
  const label = value.replace(/_/g, " ");
  return `${label.charAt(0).toUpperCase()}${label.slice(1)}`;
}

function initialEventTrigger(
  source: CircuitEventSource,
): Extract<CircuitTriggerConfig, { type: "event" }> {
  const entityType = getCircuitEventEntityTypes(source)[0]!;
  return {
    type: "event",
    source,
    entityType,
    condition: { type: getCircuitEventTypes(source, entityType)[0]! },
  };
}

function initialDefinition(): CircuitDefinition {
  return {
    entryNodeId: "agent-1",
    nodes: [{ id: "agent-1", type: "agent", prompt: "" }],
    edges: [],
  };
}

function copyDefinition(definition: CircuitDefinition): CircuitDefinition {
  return structuredClone(definition);
}

function nodeLabel(node: CircuitNode): string {
  if (node.type === "agent") return node.prompt.trim().split("\n")[0] || "Agent step";
  if (node.type === "wait") return `Wait ${node.durationSeconds} seconds`;
  return `Wait for ${node.name || "signal"}`;
}

function nextNodeId(type: CircuitNode["type"], nodes: CircuitNode[]): string {
  let index = nodes.length + 1;
  while (nodes.some((node) => node.id === `${type}-${index}`)) index += 1;
  return `${type}-${index}`;
}

function nextSignalName(nodes: CircuitNode[]): string {
  const names = new Set(
    nodes
      .filter((node): node is Extract<CircuitNode, { type: "signal" }> => node.type === "signal")
      .map((node) => node.name),
  );
  if (!names.has("approved")) return "approved";
  let index = 2;
  while (names.has(`approved-${index}`)) index += 1;
  return `approved-${index}`;
}

function newNode(type: CircuitNode["type"], id: string, nodes: CircuitNode[]): CircuitNode {
  if (type === "agent") return { id, type, prompt: "" };
  if (type === "wait") return { id, type, durationSeconds: 86_400 };
  return { id, type, name: nextSignalName(nodes) };
}

function sameCondition(left: CircuitEdge["when"], right: CircuitEdge["when"]): boolean {
  return JSON.stringify(left) === JSON.stringify(right);
}

export function wouldCreateCircuitCycle(
  definition: CircuitDefinition,
  sourceId: string,
  destinationId: string,
): boolean {
  const pending = [destinationId];
  const visited = new Set<string>();
  while (pending.length) {
    const nodeId = pending.pop()!;
    if (nodeId === sourceId) return true;
    if (visited.has(nodeId)) continue;
    visited.add(nodeId);
    for (const edge of definition.edges) {
      if (edge.from === nodeId) pending.push(edge.to);
    }
  }
  return false;
}

export function pruneUnreachableCircuitNodes(definition: CircuitDefinition): CircuitDefinition {
  const nodeIds = new Set(definition.nodes.map((node) => node.id));
  if (!nodeIds.has(definition.entryNodeId)) return definition;

  const reachable = new Set<string>();
  const pending = [definition.entryNodeId];
  while (pending.length) {
    const nodeId = pending.pop()!;
    if (reachable.has(nodeId)) continue;
    reachable.add(nodeId);
    for (const edge of definition.edges) {
      if (edge.from === nodeId && nodeIds.has(edge.to)) pending.push(edge.to);
    }
  }

  if (reachable.size === definition.nodes.length) return definition;
  return {
    ...definition,
    nodes: definition.nodes.filter((node) => reachable.has(node.id)),
    edges: definition.edges.filter((edge) => reachable.has(edge.from) && reachable.has(edge.to)),
  };
}

// undefined uses the fallback; null explicitly ends this outcome. Endings are
// represented by no matching edge and no default in the persisted contract.
export function setCircuitRoute(
  definition: CircuitDefinition,
  from: string,
  to: string | null | undefined,
  outcome?: string,
): CircuitDefinition {
  const fallback = definition.edges.find((edge) => edge.from === from && !edge.when);
  let edges = definition.edges.filter((edge) => {
    if (edge.from !== from) return true;
    return outcome === undefined ? Boolean(edge.when) : edge.when?.equals !== outcome;
  });
  if (outcome !== undefined && to === null && fallback) {
    // Materialize the other outcomes before removing the default. Otherwise
    // choosing "End circuit" would silently execute that default instead.
    edges = edges.filter((edge) => edge !== fallback);
    const node = definition.nodes.find((candidate) => candidate.id === from);
    if (node?.type === "agent") {
      for (const other of node.outcomes ?? []) {
        if (
          other === outcome ||
          edges.some((edge) => edge.from === from && edge.when?.equals === other)
        )
          continue;
        edges.push({ from, to: fallback.to, when: { path: "outcome", equals: other } });
      }
    }
  }
  if (typeof to === "string") {
    edges.push({
      from,
      to,
      ...(outcome === undefined ? {} : { when: { path: "outcome", equals: outcome } }),
    });
  }
  return pruneUnreachableCircuitNodes({ ...definition, edges });
}

export function insertCircuitStep(
  definition: CircuitDefinition,
  location: CircuitInsertLocation,
  type: CircuitNode["type"],
): { definition: CircuitDefinition; nodeId: string | null } {
  const id = nextNodeId(type, definition.nodes);
  const node = newNode(type, id, definition.nodes);
  const edges = [...definition.edges];

  if (location.kind === "start") {
    if (definition.entryNodeId !== location.to) return { definition, nodeId: null };
    edges.push({ from: id, to: location.to });
    return {
      definition: {
        ...definition,
        entryNodeId: id,
        nodes: [...definition.nodes, node],
        edges,
      },
      nodeId: id,
    };
  }

  if (location.kind === "edge") {
    const index = location.edgeIndex;
    const edge = edges[index]!;
    if (
      !edge ||
      edge.from !== location.edge.from ||
      edge.to !== location.edge.to ||
      !sameCondition(edge.when, location.edge.when)
    ) {
      return { definition, nodeId: null };
    }
    edges.splice(
      index,
      1,
      { from: edge.from, to: id, ...(edge.when ? { when: edge.when } : {}) },
      { from: id, to: edge.to },
    );
  } else {
    const routeExists = edges.some(
      (edge) => edge.from === location.from && sameCondition(edge.when, location.when),
    );
    if (routeExists || !definition.nodes.some((candidate) => candidate.id === location.from)) {
      return { definition, nodeId: null };
    }
    edges.push({
      from: location.from,
      to: id,
      ...(location.when ? { when: location.when } : {}),
    });
  }

  return {
    definition: { ...definition, nodes: [...definition.nodes, node], edges },
    nodeId: id,
  };
}

function OutcomeEditor({
  outcomes,
  onChange,
}: {
  outcomes: string[];
  onChange: (outcomes: string[]) => void;
}): React.JSX.Element {
  const [draft, setDraft] = useState("");

  function addOutcome(): void {
    const value = draft.trim();
    if (!value || outcomes.includes(value)) return;
    onChange([...outcomes, value]);
    setDraft("");
  }

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap gap-1.5">
        {outcomes.map((outcome) => (
          <span
            key={outcome}
            className="border-cyber-cyan/30 bg-cyber-cyan/10 text-cyber-cyan inline-flex items-center gap-1 rounded border px-2 py-1 text-xs"
          >
            {outcome}
            <button
              type="button"
              aria-label={`Remove ${outcome}`}
              onClick={() => onChange(outcomes.filter((value) => value !== outcome))}
              className="hover:text-cyber-text"
            >
              <X size={12} />
            </button>
          </span>
        ))}
      </div>
      <div className="flex gap-2">
        <input
          value={draft}
          onChange={(event) => setDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.preventDefault();
              addOutcome();
            }
          }}
          placeholder="e.g. MONITOR"
          className={inputClass}
        />
        <button
          type="button"
          onClick={addOutcome}
          className="border-cyber-border text-cyber-muted hover:border-cyber-cyan/40 hover:text-cyber-cyan rounded border px-3 text-xs"
        >
          Add
        </button>
      </div>
    </div>
  );
}

function NodeIdInput({
  id,
  onCommit,
}: {
  id: string;
  onCommit: (nextId: string) => boolean;
}): React.JSX.Element {
  const [draft, setDraft] = useState(id);

  function commit(): void {
    const nextId = draft.trim();
    if (!nextId) {
      setDraft(id);
      return;
    }
    if (nextId !== id && !onCommit(nextId)) setDraft(id);
  }

  return (
    <input
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
        if (event.key === "Escape") {
          setDraft(id);
          event.currentTarget.blur();
        }
      }}
      className="text-cyber-text min-w-0 flex-1 bg-transparent text-sm font-medium outline-none"
      aria-label="Step ID"
    />
  );
}

function RouteSelect({
  value,
  definition,
  sourceId,
  fallbackTarget,
  onChange,
}: {
  value: string | null | undefined;
  definition: CircuitDefinition;
  sourceId: string;
  fallbackTarget?: string;
  onChange: (value: string | null | undefined) => void;
}): React.JSX.Element {
  return (
    <select
      value={
        typeof value === "string"
          ? `node:${value}`
          : value === undefined && fallbackTarget
            ? "fallback"
            : "end"
      }
      onChange={(event) =>
        onChange(
          event.target.value === "end"
            ? null
            : event.target.value === "fallback"
              ? undefined
              : event.target.value.slice(5),
        )
      }
      className={inputClass}
    >
      <option value="end">End circuit</option>
      {fallbackTarget && <option value="fallback">Use default fallback ({fallbackTarget})</option>}
      {definition.nodes
        .filter((node) => node.id !== sourceId)
        .map((node) => {
          const createsCycle = wouldCreateCircuitCycle(definition, sourceId, node.id);
          return (
            <option key={node.id} value={`node:${node.id}`} disabled={createsCycle}>
              {node.id}: {nodeLabel(node)}
              {createsCycle ? " (would create a cycle)" : ""}
            </option>
          );
        })}
    </select>
  );
}

function StepEditorDrawer({
  node,
  definition,
  models,
  onClose,
  onUpdate,
  onRename,
  onRemove,
  onSetRoute,
  onSetOutcomes,
}: {
  node: CircuitNode;
  definition: CircuitDefinition;
  models: ModelOption[];
  onClose: () => void;
  onUpdate: (update: (node: CircuitNode) => CircuitNode) => void;
  onRename: (id: string) => boolean;
  onRemove: () => void;
  onSetRoute: (to: string | null | undefined, outcome?: string) => void;
  onSetOutcomes: (outcomes: string[]) => void;
}): React.JSX.Element {
  const defaultEdge = definition.edges.find((edge) => edge.from === node.id && !edge.when);
  const advancedEdges = definition.edges.filter((edge) => edge.from === node.id && edge.when);
  const icon =
    node.type === "agent" ? (
      <Bot size={16} />
    ) : node.type === "wait" ? (
      <Timer size={16} />
    ) : (
      <Radio size={16} />
    );

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 bg-black/35"
        onClick={onClose}
        aria-label="Close step editor"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label={`Edit ${node.id}`}
        className="border-cyber-border bg-cyber-bg fixed inset-y-0 right-0 z-50 flex w-full max-w-lg flex-col border-l shadow-2xl shadow-black/50"
      >
        <header className="border-cyber-border bg-cyber-surface/90 flex items-center gap-2 border-b px-4 py-3">
          <span className="text-cyber-cyan">{icon}</span>
          <div className="min-w-0 flex-1">
            <div className="text-cyber-muted text-[9px] tracking-[0.18em] uppercase">
              {node.type} step
            </div>
            <NodeIdInput id={node.id} onCommit={onRename} />
          </div>
          {definition.entryNodeId === node.id ? (
            <span className="border-cyber-cyan/30 bg-cyber-cyan/10 text-cyber-cyan rounded border px-1.5 py-0.5 text-[9px] tracking-wider uppercase">
              Start
            </span>
          ) : null}
          <button
            type="button"
            onClick={onRemove}
            disabled={definition.nodes.length === 1 || definition.entryNodeId === node.id}
            title={
              definition.entryNodeId === node.id ? "The start step cannot be removed" : undefined
            }
            className="text-cyber-muted hover:text-red-400 disabled:opacity-20"
            aria-label="Remove step"
          >
            <Trash2 size={15} />
          </button>
          <button
            type="button"
            onClick={onClose}
            className="text-cyber-muted hover:text-cyber-text"
            aria-label="Close step editor"
          >
            <X size={18} />
          </button>
        </header>

        <div className="min-h-0 flex-1 space-y-5 overflow-y-auto p-5">
          {node.type === "agent" && (
            <>
              <label className="text-cyber-muted block space-y-1.5 text-xs">
                <span>Prompt</span>
                <textarea
                  value={node.prompt}
                  onChange={(event) =>
                    onUpdate((current) =>
                      current.type === "agent"
                        ? { ...current, prompt: event.target.value }
                        : current,
                    )
                  }
                  rows={8}
                  placeholder="Tell Sherlog what to investigate or produce..."
                  className={`${inputClass} resize-y`}
                />
              </label>
              <label className="text-cyber-muted block space-y-1.5 text-xs">
                <span>Model override</span>
                <select
                  value={node.modelId ?? ""}
                  onChange={(event) =>
                    onUpdate((current) =>
                      current.type === "agent"
                        ? { ...current, modelId: event.target.value || undefined }
                        : current,
                    )
                  }
                  className={inputClass}
                >
                  <option value="">Use circuit owner default</option>
                  {models.map((model) => (
                    <option key={model.fullId} value={model.fullId}>
                      {model.providerName} / {model.modelName}
                    </option>
                  ))}
                </select>
              </label>
              <div className="space-y-1.5">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-cyber-muted text-xs">Expected outcomes</span>
                  <span className="text-cyber-muted/60 text-[10px]">Optional</span>
                </div>
                <OutcomeEditor outcomes={node.outcomes ?? []} onChange={onSetOutcomes} />
                <p className="text-cyber-muted/70 text-[10px]">
                  Sherlog adds the output instruction and records the selected outcome.
                </p>
              </div>
            </>
          )}

          {node.type === "wait" && (
            <label className="text-cyber-muted block space-y-1.5 text-xs">
              <span>Wait duration in seconds</span>
              <input
                type="number"
                min={0}
                value={node.durationSeconds}
                onChange={(event) =>
                  onUpdate((current) =>
                    current.type === "wait"
                      ? { ...current, durationSeconds: Number(event.target.value) }
                      : current,
                  )
                }
                className={inputClass}
              />
              <span className="text-cyber-muted/60 block text-[10px]">86,400 seconds = 1 day</span>
            </label>
          )}

          {node.type === "signal" && (
            <div className="grid gap-3">
              <label className="text-cyber-muted space-y-1.5 text-xs">
                <span>Signal name</span>
                <input
                  value={node.name}
                  onChange={(event) =>
                    onUpdate((current) =>
                      current.type === "signal"
                        ? { ...current, name: event.target.value }
                        : current,
                    )
                  }
                  className={inputClass}
                />
              </label>
              <label className="text-cyber-muted space-y-1.5 text-xs">
                <span>Timeout seconds</span>
                <input
                  type="number"
                  min={0}
                  value={node.timeoutSeconds ?? ""}
                  onChange={(event) =>
                    onUpdate((current) =>
                      current.type === "signal"
                        ? {
                            ...current,
                            timeoutSeconds: event.target.value
                              ? Number(event.target.value)
                              : undefined,
                          }
                        : current,
                    )
                  }
                  placeholder="No timeout"
                  className={inputClass}
                />
              </label>
            </div>
          )}

          <section className="border-cyber-border border-t pt-4">
            <div className="text-cyber-muted mb-3 text-[10px] tracking-wider uppercase">
              Routing
            </div>
            {node.type === "agent" && node.outcomes?.length ? (
              <div className="space-y-3">
                {node.outcomes.map((outcome) => {
                  const edge = definition.edges.find(
                    (candidate) =>
                      candidate.from === node.id &&
                      candidate.when?.path === "outcome" &&
                      candidate.when.equals === outcome,
                  );
                  return (
                    <label key={outcome} className="block space-y-1.5">
                      <span className="text-cyber-cyan font-mono text-xs">{outcome}</span>
                      <RouteSelect
                        value={edge?.to}
                        fallbackTarget={defaultEdge?.to}
                        definition={definition}
                        sourceId={node.id}
                        onChange={(to) => onSetRoute(to, outcome)}
                      />
                    </label>
                  );
                })}
                <label className="block space-y-1.5">
                  <span className="text-cyber-muted text-xs">Default fallback</span>
                  <RouteSelect
                    value={defaultEdge?.to ?? null}
                    definition={definition}
                    sourceId={node.id}
                    onChange={(to) => onSetRoute(to)}
                  />
                </label>
              </div>
            ) : (
              <label className="text-cyber-muted block space-y-1.5 text-xs">
                <span>After completion</span>
                <RouteSelect
                  value={defaultEdge?.to ?? null}
                  definition={definition}
                  sourceId={node.id}
                  onChange={(to) => onSetRoute(to)}
                />
              </label>
            )}
            {node.type !== "agent" && advancedEdges.length > 0 && (
              <p className="mt-2 text-[10px] text-amber-300/80">
                {advancedEdges.length} advanced conditional route(s) are preserved from the current
                definition.
              </p>
            )}
          </section>
        </div>
      </aside>
    </>
  );
}

function AddStepDrawer({
  location,
  onAdd,
  onClose,
}: {
  location: CircuitInsertLocation;
  onAdd: (type: CircuitNode["type"]) => void;
  onClose: () => void;
}): React.JSX.Element {
  const locationLabel =
    location.kind === "start"
      ? `Before ${location.to}`
      : location.kind === "edge"
        ? `Between ${location.edge.from} and ${location.edge.to}`
        : `After ${location.from}`;

  return (
    <>
      <button
        type="button"
        className="fixed inset-0 z-40 bg-black/35"
        onClick={onClose}
        aria-label="Close add step drawer"
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Add step"
        className="border-cyber-border bg-cyber-bg fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col border-l shadow-2xl shadow-black/50"
      >
        <header className="border-cyber-border bg-cyber-surface/90 flex items-center justify-between border-b px-5 py-4">
          <div>
            <div className="text-cyber-cyan text-[9px] tracking-[0.18em] uppercase">Add step</div>
            <h3 className="text-cyber-text mt-0.5 text-sm font-medium">{locationLabel}</h3>
          </div>
          <button
            type="button"
            onClick={onClose}
            className="text-cyber-muted hover:text-cyber-text"
            aria-label="Close add step drawer"
          >
            <X size={18} />
          </button>
        </header>
        <div className="space-y-3 p-5">
          <p className="text-cyber-muted text-xs">
            The new step will be connected automatically
            {location.kind === "edge" ? " without changing this route's condition" : ""}.
          </p>
          {(
            [
              ["agent", Bot, "Ask Sherlog to investigate or produce something"],
              ["wait", Timer, "Pause this path for a fixed duration"],
              ["signal", Radio, "Wait for an external approval or response"],
            ] as const
          ).map(([type, Icon, description]) => (
            <button
              key={type}
              type="button"
              onClick={() => onAdd(type)}
              className="border-cyber-border bg-cyber-surface/35 hover:border-cyber-cyan/50 hover:bg-cyber-cyan/5 flex w-full items-center gap-3 rounded-lg border p-4 text-left transition-colors"
            >
              <span className="border-cyber-cyan/30 bg-cyber-cyan/10 text-cyber-cyan flex h-9 w-9 shrink-0 items-center justify-center rounded border">
                <Icon size={17} />
              </span>
              <span>
                <span className="text-cyber-text block text-sm font-medium capitalize">{type}</span>
                <span className="text-cyber-muted mt-0.5 block text-xs">{description}</span>
              </span>
            </button>
          ))}
        </div>
      </aside>
    </>
  );
}

function TriggerEditor({
  trigger,
  allowChannelWideSlack,
  onChange,
  onRemove,
  onCopy,
  onRotateWebhook,
  rotatingWebhookId,
  webhookUrl,
  webhookSecret,
}: {
  trigger: CircuitTriggerConfig;
  allowChannelWideSlack: boolean;
  onChange: (trigger: CircuitTriggerConfig) => void;
  onRemove: () => void;
  onCopy: (value: string, label: string) => void;
  onRotateWebhook?: (webhookId: string) => void;
  rotatingWebhookId?: string;
  webhookUrl?: string;
  webhookSecret?: string;
}): React.JSX.Element {
  const isAdmin = useStore((state) => state.user?.role === "admin");
  const filterLocked =
    !isAdmin &&
    (trigger.type === "webhook" || (trigger.type === "event" && trigger.source === "hubspot")) &&
    Boolean(trigger.filter?.length);
  const entityTypes = trigger.type === "event" ? getCircuitEventEntityTypes(trigger.source) : [];
  const conditionTypes =
    trigger.type === "event" ? getCircuitEventTypes(trigger.source, trigger.entityType) : [];
  const conditionFields =
    trigger.type === "event"
      ? getCircuitConditionFields(trigger.source, trigger.condition.type)
      : [];

  return (
    <div className="border-cyber-border bg-cyber-bg/60 space-y-3 rounded border p-3">
      <div className="flex items-center gap-2">
        <select
          value={trigger.type}
          disabled={filterLocked}
          onChange={(event) => {
            const type = event.target.value;
            if (type === "manual") onChange({ type: "manual" });
            if (type === "cron") {
              onChange({ type: "cron", cron: "0 9 * * 1-5", timezone: defaultTimezone });
            }
            if (type === "webhook") onChange({ type: "webhook" });
            if (type === "event") {
              onChange(initialEventTrigger("linear"));
            }
          }}
          className={inputClass}
        >
          <option value="manual">Manual</option>
          <option value="cron">Schedule</option>
          <option value="webhook">Webhook</option>
          <option value="event">Event</option>
        </select>
        <button
          type="button"
          onClick={onRemove}
          disabled={filterLocked}
          className="text-cyber-muted rounded p-2 hover:bg-red-400/10 hover:text-red-400 disabled:opacity-50"
          aria-label="Remove trigger"
        >
          <Trash2 size={15} />
        </button>
      </div>

      {trigger.type === "manual" && (
        <p className="text-cyber-muted text-xs">Run this circuit from Sherlog or the Run button.</p>
      )}
      {trigger.type === "cron" && (
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-cyber-muted space-y-1 text-xs">
            <span>Cron expression</span>
            <input
              value={trigger.cron}
              onChange={(event) => onChange({ ...trigger, cron: event.target.value })}
              className={inputClass}
            />
          </label>
          <label className="text-cyber-muted space-y-1 text-xs">
            <span>Timezone</span>
            <input
              value={trigger.timezone}
              onChange={(event) => onChange({ ...trigger, timezone: event.target.value })}
              className={inputClass}
            />
          </label>
        </div>
      )}
      {trigger.type === "webhook" && (
        <div className="space-y-2">
          {trigger.webhookId ? (
            <>
              <label className="text-cyber-muted block space-y-1 text-xs">
                <span>Webhook URL</span>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={webhookUrl ?? `${window.location.origin}/webhook/${trigger.webhookId}`}
                    className={inputClass}
                  />
                  <button
                    type="button"
                    onClick={() =>
                      onCopy(
                        webhookUrl ?? `${window.location.origin}/webhook/${trigger.webhookId}`,
                        "Webhook URL",
                      )
                    }
                    className="border-cyber-border text-cyber-muted hover:text-cyber-cyan rounded border px-3"
                    aria-label="Copy webhook URL"
                  >
                    <Clipboard size={14} />
                  </button>
                </div>
              </label>
              {webhookSecret && (
                <label className="text-cyber-muted block space-y-1 text-xs">
                  <span>Bearer secret</span>
                  <div className="flex gap-2">
                    <input
                      readOnly
                      value={webhookSecret}
                      className={`${inputClass} font-mono text-xs`}
                    />
                    <button
                      type="button"
                      onClick={() => onCopy(webhookSecret, "Webhook secret")}
                      className="border-cyber-border text-cyber-muted hover:text-cyber-cyan rounded border px-3"
                      aria-label="Copy webhook secret"
                    >
                      <Clipboard size={14} />
                    </button>
                  </div>
                </label>
              )}
              {onRotateWebhook && (
                <button
                  type="button"
                  disabled={rotatingWebhookId === trigger.webhookId}
                  onClick={() => onRotateWebhook(trigger.webhookId!)}
                  className="text-cyber-muted hover:text-cyber-cyan flex items-center gap-1 text-xs disabled:opacity-50"
                >
                  <KeyRound size={13} />
                  {rotatingWebhookId === trigger.webhookId ? "Rotating..." : "Rotate secret"}
                </button>
              )}
            </>
          ) : (
            <p className="text-cyber-muted text-xs">
              Sherlog will generate the URL and secret when you save this Circuit.
            </p>
          )}
          <p className="text-cyber-muted/80 text-[11px]">
            Each matching authenticated JSON POST starts a new run. Send an optional stable
            Idempotency-Key header to make retries safe. Request bodies become untrusted input for
            agent steps. Only share this secret with senders you trust to provide agent input.
          </p>
        </div>
      )}
      {(trigger.type === "webhook" ||
        (trigger.type === "event" && trigger.source === "hubspot")) && (
        <div className="space-y-2">
          <fieldset disabled={!isAdmin} className="border-cyber-border space-y-3 border-t pt-3">
            <legend className="text-cyber-muted px-1 text-xs">Trigger filter</legend>
            <p className="text-cyber-muted/80 text-[11px]">
              All rules must match; any listed value can match within a rule. No rules accepts
              everything. Fields use payload keys or dot-paths. HubSpot matches each event in a
              batch (e.g. propertyName, propertyValue). The agent still decides relevance.
            </p>
            {(trigger.filter ?? []).map((rule, index) => (
              <div key={index} className="border-cyber-border space-y-2 rounded border p-2">
                <label className="text-cyber-muted block space-y-1 text-xs">
                  <span>Field</span>
                  <input
                    value={rule.field}
                    maxLength={CIRCUIT_WEBHOOK_MAX_FILTER_FIELD_LENGTH}
                    placeholder="e.g. propertyName"
                    onChange={(event) =>
                      onChange({
                        ...trigger,
                        filter: trigger.filter!.map((item, ruleIndex) =>
                          ruleIndex === index ? { ...item, field: event.target.value } : item,
                        ),
                      })
                    }
                    className={inputClass}
                  />
                </label>
                <label className="text-cyber-muted block space-y-1 text-xs">
                  <span>Allowed values (one per line)</span>
                  <textarea
                    value={rule.values.join("\n")}
                    rows={2}
                    placeholder="e.g. dealstage"
                    onChange={(event) =>
                      onChange({
                        ...trigger,
                        filter: trigger.filter!.map((item, ruleIndex) =>
                          ruleIndex === index
                            ? { ...item, values: event.target.value.split("\n") }
                            : item,
                        ),
                      })
                    }
                    className={inputClass}
                  />
                </label>
                <button
                  type="button"
                  onClick={() =>
                    onChange({
                      ...trigger,
                      filter: trigger.filter!.filter((_, ruleIndex) => ruleIndex !== index),
                    })
                  }
                  className="text-cyber-muted flex items-center gap-1 text-xs hover:text-red-400 disabled:opacity-50"
                >
                  <Trash2 size={13} /> Remove rule
                </button>
              </div>
            ))}
            <button
              type="button"
              disabled={(trigger.filter?.length ?? 0) >= CIRCUIT_WEBHOOK_MAX_FILTER_RULES}
              onClick={() =>
                onChange({
                  ...trigger,
                  filter: [...(trigger.filter ?? []), { field: "", values: [""] }],
                })
              }
              className="text-cyber-muted hover:text-cyber-cyan flex items-center gap-1 text-xs disabled:opacity-50"
            >
              <Plus size={13} /> Add rule
            </button>
          </fieldset>
          {!isAdmin && (
            <p className="text-cyber-muted text-[11px]">Only admins can edit filters.</p>
          )}
        </div>
      )}
      {trigger.type === "event" && (
        <div className="grid gap-3 md:grid-cols-2">
          <label className="text-cyber-muted space-y-1 text-xs">
            <span>Source</span>
            <select
              value={trigger.source}
              disabled={filterLocked}
              onChange={(event) => {
                const source = event.target.value as CircuitEventSource;
                onChange(initialEventTrigger(source));
              }}
              className={inputClass}
            >
              {CIRCUIT_EVENT_SOURCES.map((source) => (
                <option key={source} value={source}>
                  {source}
                </option>
              ))}
            </select>
          </label>
          <label className="text-cyber-muted space-y-1 text-xs">
            <span>Entity type</span>
            <select
              value={trigger.entityType}
              disabled={filterLocked}
              onChange={(event) => {
                const entityType = event.target.value;
                const repository =
                  trigger.source === "github" && typeof trigger.condition.repository === "string"
                    ? trigger.condition.repository
                    : undefined;
                onChange({
                  ...trigger,
                  entityType,
                  condition: {
                    type: getCircuitEventTypes(trigger.source, entityType)[0]!,
                    ...(repository ? { repository } : {}),
                  },
                });
              }}
              className={inputClass}
            >
              {entityTypes.map((entityType) => (
                <option key={entityType} value={entityType}>
                  {eventOptionLabel(entityType)}
                </option>
              ))}
            </select>
          </label>
          <label className="text-cyber-muted space-y-1 text-xs">
            <span>
              {trigger.source === "slack"
                ? "Slack channel ID"
                : trigger.source === "hubspot"
                  ? "Object ID"
                  : "Entity ID"}
            </span>
            <input
              value={trigger.entityId ?? ""}
              disabled={filterLocked}
              onChange={(event) =>
                onChange({ ...trigger, entityId: event.target.value || undefined })
              }
              placeholder={trigger.source === "slack" ? "e.g. C0123456789" : "Optional"}
              required={trigger.source === "slack"}
              className={inputClass}
            />
          </label>
          {trigger.source !== "slack" && trigger.source !== "hubspot" && (
            <label className="text-cyber-muted space-y-1 text-xs">
              <span>Entity identifier</span>
              <input
                value={trigger.entityIdentifier ?? ""}
                onChange={(event) =>
                  onChange({ ...trigger, entityIdentifier: event.target.value || undefined })
                }
                placeholder="e.g. ENG-123"
                className={inputClass}
              />
            </label>
          )}
          {trigger.source === "github" && (
            <label className="text-cyber-muted space-y-1 text-xs">
              <span>Repository</span>
              <input
                value={
                  typeof trigger.condition.repository === "string"
                    ? trigger.condition.repository
                    : ""
                }
                onChange={(event) =>
                  onChange({
                    ...trigger,
                    condition: {
                      ...trigger.condition,
                      repository: event.target.value,
                    },
                  })
                }
                placeholder="e.g. Bold-org/sherlog"
                required
                pattern="[^/\s]+/[^/\s]+"
                className={inputClass}
              />
            </label>
          )}
          <label className="text-cyber-muted space-y-1 text-xs">
            <span>{trigger.source === "hubspot" ? "Subscription type" : "Condition type"}</span>
            {trigger.source === "hubspot" ? (
              <input
                value={trigger.condition.type}
                disabled={filterLocked}
                onChange={(event) =>
                  onChange({ ...trigger, condition: { type: event.target.value } })
                }
                placeholder="e.g. deal.propertyChange"
                required
                className={inputClass}
              />
            ) : (
              <select
                value={trigger.condition.type}
                onChange={(event) => {
                  const repository =
                    trigger.source === "github" && typeof trigger.condition.repository === "string"
                      ? trigger.condition.repository
                      : undefined;
                  onChange({
                    ...trigger,
                    condition: {
                      type: event.target.value,
                      ...(repository ? { repository } : {}),
                    },
                  });
                }}
                className={inputClass}
              >
                {conditionTypes.map((conditionType) => (
                  <option key={conditionType} value={conditionType}>
                    {eventOptionLabel(conditionType)}
                  </option>
                ))}
              </select>
            )}
          </label>
          {trigger.source === "hubspot" && (
            <p className="text-cyber-muted text-xs md:col-span-2">
              HubSpot sends signed batches to /webhooks/hubspot. Each event can start every matching
              Circuit. Subscription types match exactly; event fields are available in
              input.event.data.
            </p>
          )}
          {conditionFields.map((field) => {
            const value = trigger.condition[field.key];
            const updateValue = (nextValue: unknown) => {
              const condition = { ...trigger.condition };
              if (nextValue === undefined || nextValue === "") delete condition[field.key];
              else condition[field.key] = nextValue;
              onChange({ ...trigger, condition });
            };
            return (
              <label key={field.key} className="text-cyber-muted space-y-1 text-xs">
                <span>{field.label}</span>
                {field.kind === "select" ? (
                  <select
                    value={typeof value === "string" ? value : ""}
                    onChange={(event) => updateValue(event.target.value)}
                    className={inputClass}
                  >
                    <option value="">Any</option>
                    {field.options?.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                ) : field.kind === "boolean" ? (
                  <select
                    value={typeof value === "boolean" ? String(value) : ""}
                    onChange={(event) =>
                      updateValue(
                        event.target.value === "" ? undefined : event.target.value === "true",
                      )
                    }
                    className={inputClass}
                  >
                    <option value="">Any</option>
                    <option value="true">Yes</option>
                    <option value="false">No</option>
                  </select>
                ) : (
                  <input
                    value={typeof value === "string" ? value : ""}
                    onChange={(event) => updateValue(event.target.value)}
                    placeholder={field.placeholder ?? "Optional"}
                    className={inputClass}
                  />
                )}
              </label>
            );
          })}
          <label className="text-cyber-muted flex items-end gap-2 pb-2 text-xs">
            <input
              type="checkbox"
              checked={trigger.recurrent ?? false}
              onChange={(event) => onChange({ ...trigger, recurrent: event.target.checked })}
            />
            Trigger for every matching event
          </label>
          <label className="text-cyber-muted space-y-1 text-xs">
            <span>Expires at</span>
            <input
              value={trigger.expiresAt ?? ""}
              onChange={(event) =>
                onChange({ ...trigger, expiresAt: event.target.value || undefined })
              }
              placeholder="Optional ISO 8601 date-time"
              className={inputClass}
            />
          </label>
          {trigger.source === "slack" && (
            <div className="border-cyber-border grid gap-3 rounded border p-3 md:col-span-2 md:grid-cols-3">
              {(() => {
                const text =
                  trigger.condition.text &&
                  typeof trigger.condition.text === "object" &&
                  !Array.isArray(trigger.condition.text)
                    ? trigger.condition.text
                    : null;
                const textValue = text ? String(Reflect.get(text, "value") ?? "") : "";
                const textOp = text ? String(Reflect.get(text, "op") ?? "contains") : "contains";
                const caseSensitive = text ? Reflect.get(text, "caseSensitive") !== false : true;
                const updateText = (value: string, op = textOp, matchCase = caseSensitive) => {
                  const condition = { ...trigger.condition };
                  if (value) {
                    condition.text = {
                      op,
                      value,
                      caseSensitive: matchCase,
                    };
                  } else {
                    delete condition.text;
                  }
                  onChange({ ...trigger, condition });
                };
                return (
                  <>
                    <label className="text-cyber-muted space-y-1 text-xs md:col-span-2">
                      <span>Message text{allowChannelWideSlack ? " (optional)" : ""}</span>
                      <input
                        value={textValue}
                        onChange={(event) => updateText(event.target.value)}
                        placeholder="Optional when another discriminator is set"
                        className={inputClass}
                      />
                    </label>
                    <label className="text-cyber-muted space-y-1 text-xs">
                      <span>Text match</span>
                      <select
                        value={textOp}
                        disabled={!textValue}
                        onChange={(event) => updateText(textValue, event.target.value)}
                        className={inputClass}
                      >
                        {CIRCUIT_EVENT_TEXT_OPERATORS.map((operator) => (
                          <option key={operator} value={operator}>
                            {eventOptionLabel(operator)}
                          </option>
                        ))}
                      </select>
                    </label>
                    <label className="text-cyber-muted flex items-center gap-2 text-xs md:col-span-3">
                      <input
                        type="checkbox"
                        checked={caseSensitive}
                        disabled={!textValue}
                        onChange={(event) => updateText(textValue, textOp, event.target.checked)}
                      />
                      Match case
                    </label>
                  </>
                );
              })()}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export function CircuitEditor({
  circuit,
  ownerScope,
  onClose,
  onSaved,
}: CircuitEditorProps): React.JSX.Element {
  const showToast = useStore((state) => state.showToast);
  const createCircuit = useCreateCircuit();
  const updateCircuit = useUpdateCircuit();
  const rotateWebhook = useRotateCircuitWebhook();
  const { data: modelsData } = useModels();
  const [name, setName] = useState(circuit?.name ?? "");
  const [description, setDescription] = useState(circuit?.description ?? "");
  const [cronOverlapPolicy, setCronOverlapPolicy] = useState<CircuitCronOverlapPolicy>(
    circuit?.cronOverlapPolicy ?? "skip",
  );
  const [definition, setDefinition] = useState(() =>
    copyDefinition(circuit?.definition ?? initialDefinition()),
  );
  const deferredDefinition = useDeferredValue(definition);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [insertLocation, setInsertLocation] = useState<CircuitInsertLocation | null>(null);
  const [triggers, setTriggers] = useState<CircuitTriggerConfig[]>(
    () =>
      circuit?.triggers.map((trigger) => structuredClone(trigger.config)) ?? [{ type: "manual" }],
  );
  const [rotatedWebhookSecrets, setRotatedWebhookSecrets] = useState<Record<string, string>>({});
  const pending = createCircuit.isPending || updateCircuit.isPending || rotateWebhook.isPending;
  const selectedNode = definition.nodes.find((node) => node.id === selectedNodeId) ?? null;

  async function copyValue(value: string, label: string): Promise<void> {
    try {
      await navigator.clipboard.writeText(value);
      showToast(`${label} copied`, "success");
    } catch {
      showToast(`Could not copy ${label.toLowerCase()}`, "error");
    }
  }

  async function rotateWebhookSecret(webhookId: string): Promise<void> {
    if (
      !circuit ||
      !window.confirm("Rotate this webhook secret? The current secret will stop working.")
    ) {
      return;
    }
    try {
      const result = await rotateWebhook.mutateAsync({ id: circuit.id, webhookId });
      setRotatedWebhookSecrets((current) => ({
        ...current,
        [webhookId]: result.webhook.secret,
      }));
      showToast("Webhook secret rotated", "success");
    } catch (error) {
      showToast(
        error instanceof Error ? error.message : "Failed to rotate webhook secret",
        "error",
      );
    }
  }

  function updateNode(id: string, update: (node: CircuitNode) => CircuitNode): void {
    setDefinition((current) => ({
      ...current,
      nodes: current.nodes.map((node) => (node.id === id ? update(node) : node)),
    }));
  }

  function renameNode(oldId: string, id: string): boolean {
    if (definition.nodes.some((node) => node.id === id && node.id !== oldId)) {
      showToast(`Step ID ${id} is already in use`, "error");
      return false;
    }
    setDefinition((current) => ({
      entryNodeId: current.entryNodeId === oldId ? id : current.entryNodeId,
      nodes: current.nodes.map((node) => (node.id === oldId ? { ...node, id } : node)),
      edges: current.edges.map((edge) => ({
        ...edge,
        from: edge.from === oldId ? id : edge.from,
        to: edge.to === oldId ? id : edge.to,
      })),
    }));
    return true;
  }

  function setOutcomes(id: string, outcomes: string[]): void {
    setDefinition((current) => {
      const currentNode = current.nodes.find((node) => node.id === id);
      if (currentNode?.type !== "agent") return current;
      const removed = (currentNode.outcomes ?? []).filter((outcome) => !outcomes.includes(outcome));
      const nextDefinition: CircuitDefinition = {
        ...current,
        nodes: current.nodes.map((node) =>
          node.id === id && node.type === "agent"
            ? { ...node, ...(outcomes.length ? { outcomes } : { outcomes: undefined }) }
            : node,
        ),
        edges: removed.length
          ? current.edges.filter(
              (edge) =>
                edge.from !== id || !edge.when || !removed.includes(String(edge.when.equals)),
            )
          : current.edges,
      };
      return pruneUnreachableCircuitNodes(nextDefinition);
    });
  }

  function setRoute(from: string, to: string | null | undefined, outcome?: string): void {
    setDefinition((current) => setCircuitRoute(current, from, to, outcome));
  }

  function addNodeAtLocation(type: CircuitNode["type"]): void {
    if (!insertLocation) return;
    const result = insertCircuitStep(definition, insertLocation, type);
    if (!result.nodeId) {
      showToast("That route changed. Choose its + button again.", "error");
      setInsertLocation(null);
      return;
    }
    setDefinition(result.definition);
    setInsertLocation(null);
    setSelectedNodeId(result.nodeId);
  }

  function removeNode(id: string): void {
    setDefinition((current) => {
      if (current.nodes.length === 1 || current.entryNodeId === id) return current;
      return pruneUnreachableCircuitNodes({
        ...current,
        nodes: current.nodes.filter((node) => node.id !== id),
        edges: current.edges.filter((edge) => edge.from !== id && edge.to !== id),
      });
    });
  }

  async function save(): Promise<void> {
    try {
      if (circuit) {
        const changes: {
          name?: string;
          cronOverlapPolicy?: CircuitCronOverlapPolicy;
          description?: string;
          definition?: CircuitDefinition;
          triggers?: CircuitTriggerConfig[];
        } = {};
        if (name !== circuit.name) changes.name = name;
        if (cronOverlapPolicy !== circuit.cronOverlapPolicy)
          changes.cronOverlapPolicy = cronOverlapPolicy;
        if ((description.trim() || null) !== circuit.description) changes.description = description;
        if (JSON.stringify(definition) !== JSON.stringify(circuit.definition)) {
          changes.definition = definition;
        }
        if (
          JSON.stringify(triggers) !==
          JSON.stringify(circuit.triggers.map((trigger) => trigger.config))
        ) {
          changes.triggers = triggers;
        }
        if (Object.keys(changes).length === 0) {
          onSaved(circuit);
          return;
        }
        const result = await updateCircuit.mutateAsync({ id: circuit.id, ...changes });
        showToast("Circuit updated", "success");
        onSaved(result.circuit);
        return;
      }

      const result = await createCircuit.mutateAsync({
        ownerScope,
        name,
        cronOverlapPolicy,
        description,
        definition,
        triggers,
      });
      showToast(circuit ? "Circuit updated" : "Circuit created", "success");
      onSaved(result.circuit);
    } catch (error) {
      showToast(error instanceof Error ? error.message : "Failed to save circuit", "error");
    }
  }

  return (
    <div className="bg-cyber-bg flex min-h-0 flex-1 flex-col">
      <header className="border-cyber-border bg-cyber-surface/90 flex items-center justify-between gap-3 border-b px-4 py-3 md:px-6">
        <div className="flex min-w-0 items-center gap-2">
          <SidebarToggleButton />
          <div className="min-w-0">
            <div className="text-cyber-cyan text-[10px] tracking-[0.22em] uppercase">
              Circuit builder
            </div>
            <h2 className="text-cyber-text truncate text-base font-semibold">
              {circuit ? `Edit ${circuit.name}` : "Create durable circuit"}
            </h2>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onClose}
            disabled={pending}
            className="border-cyber-border text-cyber-muted hover:text-cyber-text rounded border px-3 py-2 text-xs disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={save}
            disabled={pending}
            className="bg-cyber-cyan/15 text-cyber-cyan border-cyber-cyan/40 hover:bg-cyber-cyan/25 flex items-center gap-1.5 rounded border px-3 py-2 text-xs disabled:opacity-50"
          >
            <Save size={14} /> {pending ? "Saving..." : "Save circuit"}
          </button>
        </div>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        <main className="mx-auto grid w-full max-w-7xl gap-6 px-4 py-6 lg:grid-cols-[minmax(0,1fr)_320px] lg:px-6">
          <div className="space-y-5">
            <section className="border-cyber-border bg-cyber-surface/30 grid gap-4 rounded-lg border p-4 md:grid-cols-2">
              <label className="text-cyber-muted space-y-1.5 text-xs">
                <span>Name</span>
                <input
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Redemption monitoring"
                  className={inputClass}
                />
              </label>
              <label className="text-cyber-muted space-y-1.5 text-xs">
                <span>Description</span>
                <input
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="What this circuit does"
                  className={inputClass}
                />
              </label>
            </section>

            <section>
              <div className="mb-3">
                <h3 className="text-cyber-text flex items-center gap-2 text-sm font-medium">
                  <GitBranch size={15} className="text-cyber-cyan" /> Circuit topology
                </h3>
                <p className="text-cyber-muted mt-1 text-xs">
                  Select a step to edit it. Use + on any route or ending to insert a connected step.
                </p>
              </div>
              <CircuitGraph
                definition={deferredDefinition}
                selectedNodeId={selectedNodeId}
                onSelectNode={(nodeId) => {
                  setInsertLocation(null);
                  setSelectedNodeId(nodeId);
                }}
                onInsert={(location) => {
                  setSelectedNodeId(null);
                  setInsertLocation(location);
                }}
              />
            </section>
          </div>

          <aside className="space-y-5 lg:sticky lg:top-5 lg:self-start">
            <section className="border-cyber-border bg-cyber-surface/35 rounded-lg border p-4">
              <div className="mb-3 flex items-center justify-between">
                <h3 className="text-cyber-text flex items-center gap-2 text-sm font-medium">
                  <CalendarClock size={15} className="text-cyber-cyan" /> Triggers
                </h3>
                <button
                  type="button"
                  onClick={() => setTriggers((current) => [...current, { type: "manual" }])}
                  className="text-cyber-muted hover:text-cyber-cyan flex items-center gap-1 text-xs"
                >
                  <Plus size={13} /> Add
                </button>
              </div>
              <div className="space-y-2">
                {triggers.map((trigger, index) => (
                  <TriggerEditor
                    key={index}
                    trigger={trigger}
                    allowChannelWideSlack={ownerScope === "system"}
                    onChange={(value) =>
                      setTriggers((current) =>
                        current.map((item, itemIndex) => (itemIndex === index ? value : item)),
                      )
                    }
                    onRemove={() =>
                      setTriggers((current) =>
                        current.filter((_, itemIndex) => itemIndex !== index),
                      )
                    }
                    onCopy={(value, label) => void copyValue(value, label)}
                    onRotateWebhook={
                      circuit ? (webhookId) => void rotateWebhookSecret(webhookId) : undefined
                    }
                    rotatingWebhookId={
                      rotateWebhook.isPending ? rotateWebhook.variables?.webhookId : undefined
                    }
                    webhookUrl={
                      trigger.type === "webhook" && trigger.webhookId
                        ? circuit?.triggers.find(
                            (candidate) =>
                              candidate.config.type === "webhook" &&
                              candidate.config.webhookId === trigger.webhookId,
                          )?.webhookUrl
                        : undefined
                    }
                    webhookSecret={
                      trigger.type === "webhook" && trigger.webhookId
                        ? (rotatedWebhookSecrets[trigger.webhookId] ??
                          circuit?.triggers.find(
                            (candidate) =>
                              candidate.config.type === "webhook" &&
                              candidate.config.webhookId === trigger.webhookId,
                          )?.webhookSecret)
                        : undefined
                    }
                  />
                ))}
                {triggers.some((trigger) => trigger.type === "cron") && (
                  <label className="text-cyber-muted border-cyber-border block space-y-2 border-t pt-3 text-xs">
                    <span className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        checked={cronOverlapPolicy === "allow"}
                        onChange={(event) =>
                          setCronOverlapPolicy(event.target.checked ? "allow" : "skip")
                        }
                      />
                      Allow overlapping scheduled runs
                    </span>
                    <span className="block text-[10px]">
                      By default, skip occurrences while an earlier scheduled run is active
                      (including waits). This applies across all cron triggers. Manual, webhook, and
                      event runs remain independent.
                    </span>
                  </label>
                )}
                {triggers.length === 0 && (
                  <p className="text-cyber-muted text-xs">
                    A manual trigger will be added on save.
                  </p>
                )}
              </div>
            </section>

            <details className="border-cyber-border bg-cyber-surface/25 rounded-lg border">
              <summary className="text-cyber-muted hover:text-cyber-text flex cursor-pointer items-center gap-2 px-4 py-3 text-xs">
                <Braces size={14} /> Definition preview
              </summary>
              <pre className="border-cyber-border text-cyber-muted max-h-80 overflow-auto border-t p-3 text-[10px]">
                {JSON.stringify({ definition, triggers }, null, 2)}
              </pre>
            </details>
          </aside>
        </main>
      </div>

      {selectedNode && (
        <StepEditorDrawer
          key={selectedNode.id}
          node={selectedNode}
          definition={definition}
          models={modelsData?.models ?? []}
          onClose={() => setSelectedNodeId(null)}
          onUpdate={(update) => updateNode(selectedNode.id, update)}
          onRename={(id) => {
            const renamed = renameNode(selectedNode.id, id);
            if (renamed) setSelectedNodeId(id);
            return renamed;
          }}
          onRemove={() => {
            if (
              definition.nodes.length > 1 &&
              window.confirm(`Remove ${selectedNode.id} and its connected routes?`)
            ) {
              removeNode(selectedNode.id);
              setSelectedNodeId(null);
            }
          }}
          onSetRoute={(to, outcome) => setRoute(selectedNode.id, to, outcome)}
          onSetOutcomes={(outcomes) => setOutcomes(selectedNode.id, outcomes)}
        />
      )}
      {insertLocation && (
        <AddStepDrawer
          location={insertLocation}
          onAdd={addNodeAtLocation}
          onClose={() => setInsertLocation(null)}
        />
      )}
    </div>
  );
}
